'use client';

import React, { useId } from 'react';
import { copy } from '@/lib/ethics/copy';

interface PhotoConsentCheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** 'book' shows the book photo terms (C-02), 'free-cover' the free-cover ones (C-03). */
  variant: 'book' | 'free-cover';
  /** A photo was picked before the tick: show C-04 under the box. */
  error?: boolean;
}

/**
 * The required photo-consent tick (R1/R3). Unticked by default: the parent ticks it themselves. The label wraps
 * the native input, so the whole sentence is clickable; the error is tied to the input with aria-describedby.
 */
export function PhotoConsentCheckbox({ checked, onChange, variant, error = false }: PhotoConsentCheckboxProps) {
  const id = useId();
  const termsId = `${id}-terms`;
  const errorId = `${id}-error`;
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 text-left" data-photo-consent>
      <label className="flex cursor-pointer items-start gap-2 text-sm font-semibold text-ink">
        <input
          type="checkbox"
          name="photoConsent"
          required
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-invalid={error || undefined}
          aria-describedby={error ? `${errorId} ${termsId}` : termsId}
          className="mt-0.5 h-4 w-4 shrink-0 accent-marigold-deep"
        />
        <span>{copy('C-01')}</span>
      </label>
      <p id={termsId} className="mt-1 pl-6 text-xs text-gray-500">
        {copy(variant === 'free-cover' ? 'C-03' : 'C-02')}{' '}
        {/* /about renders HOW_WE_MAKE_YOUR_BOOK under this anchor (__tests__/about-page.test.tsx). */}
        <a href="/about#how-we-make-your-book" className="text-ink underline hover:text-ink-soft">
          How we make your book →
        </a>
      </p>
      {error && (
        <p id={errorId} role="alert" className="mt-1 pl-6 text-sm text-red-600">
          {copy('C-04')}
        </p>
      )}
    </div>
  );
}
